import { ColumnType } from '../types';
import { TableViewState, SettingsItem } from './types';
import {
  effectiveConfigurable, effectivePinnable, isGroup, isSystem, labelOf, stateKeyOf,
} from './columns';
import { isLeafVisible } from './visibility';
import { isLeafPinned } from './pinning';

/**
 * Build the settings-panel item model from columns and the draft state, so the
 * panel stays purely presentational. System columns are excluded; columns
 * sharing a `settingsKey` collapse to one item. Child flags reflect effective
 * (group-cascaded) meta. Group items aggregate their children: `visible` if
 * any child is visible, `pinned` if every child is, `canToggle`/`canPin` if
 * any child can — capability comes bottom-up from effective child meta, so a
 * group whose children are all locked reports itself locked. A sub-group
 * nested at depth ≥ 3 is an atomic unit and appears as a single child item.
 */
export function deriveSettingsItems<R>(
  columns: ColumnType<R>[],
  draft?: TableViewState | null,
): SettingsItem[] {
  const items: SettingsItem[] = [];
  const seen = new Set<string>();
  for (const col of columns) {
    if (isSystem(col)) continue;
    const item = isGroup(col) ? groupItem(col, draft, seen) : leafItem(col, draft, seen);
    if (item) items.push(item);
  }
  return items;
}

function leafItem<R>(
  leaf: ColumnType<R>,
  draft: TableViewState | null | undefined,
  seen: Set<string>,
  parent?: ColumnType<R>,
): SettingsItem | null {
  const key = stateKeyOf(leaf);
  if (seen.has(key)) return null;
  seen.add(key);
  return {
    key,
    label: labelOf(leaf),
    visible: isLeafVisible(leaf, draft, parent),
    pinned: isLeafPinned(leaf, draft, parent),
    canToggle: effectiveConfigurable(leaf, parent),
    canPin: effectivePinnable(leaf, parent),
    canReorder: leaf.reorderable !== false,
  };
}

function groupItem<R>(
  group: ColumnType<R>,
  draft: TableViewState | null | undefined,
  seen: Set<string>,
): SettingsItem | null {
  const key = stateKeyOf(group);
  if (seen.has(key)) return null;
  seen.add(key);

  const children: SettingsItem[] = [];
  const childSeen = new Set<string>();
  for (const child of group.children ?? []) {
    if (isSystem(child)) continue;
    const item = leafItem(child, draft, childSeen, group);
    if (item) children.push(item);
  }

  return {
    key,
    label: labelOf(group),
    visible: children.some((child) => child.visible),
    pinned: children.length > 0 && children.every((child) => child.pinned),
    canToggle: children.some((child) => child.canToggle),
    canPin: children.some((child) => child.canPin),
    canReorder: group.reorderable !== false,
    children,
  };
}
